import React, { useEffect, useState, useCallback } from 'react';
import {
  HiEye, HiEyeOff, HiChevronDown, HiChevronRight,
  HiCheck, HiX, HiExclamation,
} from 'react-icons/hi';
import { settingsService } from '../services/api';

// ----- Single field row -----
const FieldRow = ({ field, draft, onChange }) => {
  const [showValue, setShowValue] = useState(false);
  const isDirty = draft !== (field.value ?? '');

  return (
    <div className="flex items-start gap-4 py-3 border-b border-slate-100 last:border-0">
      {/* Key + description */}
      <div className="w-64 shrink-0 pt-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-mono text-slate-700">{field.key}</span>
          {field.needs_restart && (
            <span className="text-[10px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded font-medium leading-none">
              restart
            </span>
          )}
        </div>
        {field.description && (
          <p className="text-xs text-slate-400 mt-0.5">{field.description}</p>
        )}
      </div>

      {/* Input */}
      <div className="flex-1 min-w-0 relative">
        <input
          type={field.sensitive && !showValue ? 'password' : 'text'}
          value={draft}
          onChange={e => onChange(field.key, e.target.value)}
          className={`w-full text-sm font-mono border rounded-lg px-3 py-1.5 pr-8 focus:outline-none focus:ring-2 transition-colors ${
            isDirty
              ? 'border-blue-400 bg-blue-50/30 focus:ring-blue-400/20'
              : 'border-slate-200 bg-white focus:ring-blue-400/20'
          }`}
          placeholder={field.sensitive ? '••••••••' : ''}
        />
        {field.sensitive && (
          <button
            type="button"
            onClick={() => setShowValue(v => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            tabIndex={-1}
          >
            {showValue ? <HiEyeOff className="w-4 h-4" /> : <HiEye className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
};

// ----- Group card -----
const GroupCard = ({ group, drafts, onDraftChange }) => {
  const [open, setOpen] = useState(true);
  const dirtyCount = group.fields.filter(f => drafts[f.key] !== (f.value ?? '')).length;
  const restartCount = group.fields.filter(f => f.needs_restart && drafts[f.key] !== (f.value ?? '')).length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? <HiChevronDown className="w-4 h-4 text-slate-400" /> : <HiChevronRight className="w-4 h-4 text-slate-400" />}
          <span className="font-semibold text-slate-800 text-sm">{group.group}</span>
          <span className="text-xs text-slate-400 font-normal ml-1">{group.fields.length} keys</span>
          {dirtyCount > 0 && (
            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
              {dirtyCount} changed
            </span>
          )}
          {restartCount > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium flex items-center gap-1">
              <HiExclamation className="w-3 h-3" /> restart needed
            </span>
          )}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-1">
          {group.fields.map(field => (
            <FieldRow
              key={field.key}
              field={field}
              draft={drafts[field.key] ?? (field.value ?? '')}
              onChange={onDraftChange}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ----- Page -----
const SettingsPage = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [restartRequired, setRestartRequired] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const initDrafts = (groupList) => {
    const init = {};
    groupList.forEach(g => g.fields.forEach(f => { init[f.key] = f.value ?? ''; }));
    setDrafts(init);
  };

  const loadSettings = useCallback(() => {
    settingsService.getAll()
      .then(data => { setGroups(data.groups); initDrafts(data.groups); })
      .catch(err => setError(err.response?.data?.detail || 'Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleDraftChange = (key, value) => {
    setSaveError(null);
    setRestartRequired(false);
    setSavedAt(null);
    setDrafts(prev => ({ ...prev, [key]: value }));
  };

  // Compute dirty changes
  const dirtyChanges = {};
  groups.forEach(g => g.fields.forEach(f => {
    const current = f.value ?? '';
    if (drafts[f.key] !== undefined && drafts[f.key] !== current) {
      dirtyChanges[f.key] = drafts[f.key];
    }
  }));
  const hasDirty = Object.keys(dirtyChanges).length > 0;

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const data = await settingsService.batchUpdate(dirtyChanges);
      setGroups(data.groups);
      initDrafts(data.groups);
      setRestartRequired(data.requires_restart ?? false);
      setSavedAt(new Date());
    } catch (e) {
      setSaveError(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setSaveError(null);
    setRestartRequired(false);
    setSavedAt(null);
    initDrafts(groups);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-28 bg-slate-200 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl">
      {/* Header row */}
      <div className="mb-5">
        <h2 className="text-2xl font-bold text-slate-800">Settings</h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Values marked{' '}
          <span className="bg-amber-50 text-amber-700 text-[10px] px-1 py-0.5 rounded font-medium align-middle">
            restart
          </span>{' '}
          are written to <span className="font-mono">.env</span> and only take effect after a container restart.
          Folder prefixes live on the Sync Sources page.
        </p>
      </div>

      {/* Sticky action bar: the settings list is long, and a Save button that
          scrolls out of reach means edits get abandoned halfway down. */}
      {hasDirty && (
        <div className="sticky top-0 z-20 -mx-1 px-1 py-2 mb-4 bg-slate-50/95 backdrop-blur">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm">
            <span className="text-sm text-slate-600 mr-auto">
              {Object.keys(dirtyChanges).length} unsaved change{Object.keys(dirtyChanges).length > 1 ? 's' : ''}
            </span>
            <button
              onClick={handleDiscard}
              disabled={saving}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <HiX className="w-4 h-4" aria-hidden="true" /> Discard
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 px-3 py-1.5 rounded-lg transition-colors"
            >
              <HiCheck className="w-4 h-4" aria-hidden="true" />
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}

      {/* Restart banner */}
      {restartRequired && (
        <div className="mb-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <HiExclamation className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Container restart required</p>
            <p className="text-sm text-amber-700 mt-0.5">
              Settings saved to <span className="font-mono">.env</span>. Run{' '}
              <span className="font-mono bg-amber-100 px-1 rounded">docker-compose restart</span> for changes to take effect.
            </p>
          </div>
        </div>
      )}

      {/* Saved confirmation */}
      {savedAt && !restartRequired && (
        <div className="mb-4 flex items-center gap-2 bg-emerald-50 ring-1 ring-emerald-100 rounded-xl px-4 py-3">
          <HiCheck className="w-5 h-5 text-emerald-600 shrink-0" aria-hidden="true" />
          <p className="text-sm text-emerald-800 font-medium">Settings saved</p>
        </div>
      )}

      {saveError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">{saveError}</div>
      )}

      <div className="space-y-3">
        {groups.map(group => (
          <GroupCard key={group.group} group={group} drafts={drafts} onDraftChange={handleDraftChange} />
        ))}
      </div>
    </div>
  );
};

export default SettingsPage;
